import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Skeleton,
  Chip,
} from "@mui/material";
import { formatCurrency } from "../../utils/formatters";

/**
 * Component to display the order summary in the checkout sidebar.
 * Can display summary based on a cart OR a completed order object.
 * Updated to show wallet credit applied.
 * @param {object} props - Component props
 * @param {object | null} [props.cart] - The user's cart object (used before order completion).
 * @param {object | null} [props.order] - The completed order object (used on step 2).
 * @param {object} props.checkoutData - Current checkout data (for shipping, payment method, and potentially applied wallet amount).
 * @param {boolean} [props.isLoading] - Indicates if order data is being loaded (for step 2).
 * @param {string | null} [props.error] - Error message if order loading failed (for step 2).
 * @param {number} [props.amountAppliedByWallet=0] - Amount applied from wallet (passed from AddressStep).
 */
const OrderSummary = ({
  cart,
  order,
  checkoutData,
  isLoading = false,
  error = null,
  amountAppliedByWallet = 0, // Receive the applied amount as a prop
}) => {
  const { t } = useTranslation();
  const { t: tCheckout } = useTranslation("checkout");
  const { taxRate } = useSelector((state) => state.tax);
  const { selectedMethod } = useSelector((state) => state.shipping);

  // Determine the source of truth: completed order or cart
  const isOrderComplete = !!order;
  const dataSource = order || cart;

  // --- Calculate Totals ---
  // Use memoization for calculations to avoid re-running on every render unless inputs change
  const calculatedTotals = useMemo(() => {
    const items = dataSource?.items || [];
    const subtotal = parseFloat(order?.subtotal || cart?.subtotal || 0);

    // Calculate shipping based on selected shipping method
    let shippingCost = 0;
    let freeShippingApplied = false;
    let shippingMethodName = "";
    let estimatedDeliveryTime = "";

    if (isOrderComplete) {
      // For completed orders, use the shipping cost from the order
      shippingCost = parseFloat(order.shipping || 0);
      shippingMethodName = order.shippingMethodName || "";
      estimatedDeliveryTime = order.estimatedDeliveryTime || "";
    } else if (selectedMethod) {
      // For cart, use the selected shipping method
      shippingCost = parseFloat(selectedMethod.price || 0);
      freeShippingApplied = selectedMethod.freeShippingApplied || false;
      shippingMethodName = selectedMethod.name || "";
      estimatedDeliveryTime = selectedMethod.estimatedDeliveryTime || "";
    }

    const discount = parseFloat(order?.discount || cart?.discount || 0); // Assuming cart might have discount
    const tax = parseFloat(order?.tax || cart?.tax || 0); // Assuming cart might have calculated tax

    // Initial total before wallet
    const initialTotal = parseFloat((subtotal + shippingCost + tax - discount).toFixed(2));

    // Swiss 5-centime rounding — always round UP to nearest 0.05 (works in cents to avoid float issues)
    const roundedTotal = (Math.ceil(Math.round(initialTotal * 100) / 5) * 5) / 100;
    const roundingAdjustment = isOrderComplete
      ? parseFloat(order.roundingAdjustment || 0)
      : parseFloat((roundedTotal - initialTotal).toFixed(2));

    // Amount due after wallet application (uses rounded total)
    const amountDue = Math.max(0, roundedTotal - amountAppliedByWallet);

    // Get tax rate for display from order, cart, or store
    let displayTaxRate = 0;
    if (order?.taxRate) {
      displayTaxRate = parseFloat(order.taxRate) * 100; // Convert from decimal to percentage
    } else if (cart?.taxRate) {
      displayTaxRate = parseFloat(cart.taxRate) * 100; // Convert from decimal to percentage
    } else if (taxRate?.rate) {
      displayTaxRate = parseFloat(taxRate.rate) * 100; // Convert from decimal to percentage
    } else {
      // Default fallback
      displayTaxRate =
        tax > 0 && subtotal - discount > 0
          ? (tax / (subtotal - discount)) * 100
          : 7.7; // Default to 7.7% if all else fails
    }

    return {
      items,
      subtotal,
      shippingCost,
      freeShippingApplied,
      shippingMethodName,
      estimatedDeliveryTime,
      discount,
      tax,
      roundingAdjustment,
      taxRateDisplay: displayTaxRate,
      initialTotal,
      amountDue,
    };
    // Dependencies: order, cart, selectedMethod, amountAppliedByWallet, taxRate
  }, [order, cart, selectedMethod, amountAppliedByWallet, taxRate]);

  // Ensure formatCurrency is applied to all monetary values
  const currency = order?.currency || "CHF"; // Default to CHF if no currency specified in order

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {isOrderComplete
            ? tCheckout("orderSummary")
            : tCheckout("cartSummary")}
        </Typography>

        {/* Loading and Error States for Completed Order */}
        {isOrderComplete && isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 1 }}>
              {tCheckout("loadingOrderDetails")}
            </Typography>
          </Box>
        )}
        {isOrderComplete && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Items List - Handle both cart items and order items */}
        {!isLoading && !error && calculatedTotals.items.length > 0
          ? calculatedTotals.items.map((item) => {
              // Determine unique key: cart items use productId + options, order items use id
              const itemKey = isOrderComplete
                ? item.id
                : item.productId + JSON.stringify(item.options || {});
              const productName =
                item.product?.name || tCheckout("productNameMissing");
              const itemPrice = parseFloat(item.price) || 0; // Price per unit
              const itemQuantity = item.quantity || 0;
              const itemTotal = itemPrice * itemQuantity;

              return (
                <Box
                  key={itemKey}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ flexGrow: 1, mr: 1 }}>
                    {productName} x{itemQuantity}
                    {/* TODO: Display options from item.options if needed */}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    {formatCurrency(itemTotal, currency)}
                  </Typography>
                </Box>
              );
            })
          : !isLoading &&
            !error && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isOrderComplete
                  ? tCheckout("noItemsInOrder")
                  : tCheckout("cartIsEmpty")}
              </Typography>
            )}

        {/* Totals Section - Only show if not loading/error and items exist or order is complete */}
        {!isLoading &&
          !error &&
          (calculatedTotals.items.length > 0 || isOrderComplete) && (
            <>
              <Divider sx={{ my: 2 }} />

              {/* Subtotal */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body1">{tCheckout("subtotal")}</Typography>
                <Typography variant="body1">
                  {formatCurrency(calculatedTotals.subtotal, currency)}
                </Typography>
              </Box>

              {/* Shipping */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Box>
                  <Typography variant="body1">
                    {tCheckout("shipping")}
                    {calculatedTotals.shippingMethodName && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                        sx={{ ml: 1 }}
                      >
                        ({calculatedTotals.shippingMethodName})
                      </Typography>
                    )}
                  </Typography>
                  {calculatedTotals.estimatedDeliveryTime && (
                    <Typography variant="body2" color="text.secondary">
                      {tCheckout("estimatedDelivery")}:{" "}
                      {calculatedTotals.estimatedDeliveryTime}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {calculatedTotals.freeShippingApplied ? (
                    <Chip
                      label={tCheckout("freeShipping")}
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1">
                      {formatCurrency(calculatedTotals.shippingCost, currency)}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Discount */}
              {calculatedTotals.discount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    color: "success.main",
                  }}
                >
                  <Typography variant="body1">
                    {dataSource?.couponCode
                      ? tCheckout("discountWithCode", {
                          code: dataSource.couponCode,
                        })
                      : tCheckout("discount")}
                  </Typography>
                  <Typography variant="body1">
                    -{formatCurrency(calculatedTotals.discount, currency)}
                  </Typography>
                </Box>
              )}

              {/* Tax */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body1">
                  {tCheckout("taxWithRate", {
                    rate:
                      calculatedTotals.taxRateDisplay > 0
                        ? calculatedTotals.taxRateDisplay.toFixed(1)
                        : 0,
                  })}
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(calculatedTotals.tax, currency)}
                </Typography>
              </Box>

              {/* Wallet Credit Applied */}
              {amountAppliedByWallet > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    color: "success.main",
                  }}
                >
                  <Typography variant="body1">
                    {tCheckout("walletCreditApplied")}
                  </Typography>
                  <Typography variant="body1">
                    -{formatCurrency(amountAppliedByWallet, currency)}
                  </Typography>
                </Box>
              )}

              {/* Swiss Rounding Adjustment */}
              {calculatedTotals.roundingAdjustment !== 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {tCheckout("rounding")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(calculatedTotals.roundingAdjustment, currency)}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Total Due */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {tCheckout("totalDue")}
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(calculatedTotals.amountDue, currency)}
                </Typography>
              </Box>
            </>
          )}
      </CardContent>
    </Card>
  );
};

export default OrderSummary;
